import { useMemo } from 'react';
import { useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query';
import { getRaw } from 'services/api';
import { INITIAL_DISPLAY_MAX_COUNT, MAX_DISPLAY_COUNT } from '../Launchpad.constants';
import { ActivityItems } from '../Launchpad.types';

const USER_ACTIVITY_URL = '/userActivity';
const LOADING_STATUS = 'loading';

type LaunchpadResponse = {
  results: ActivityItems;
  total: number;
};

type LaunchpadParams = {
  resourceTypes?: string[];
  actionTypes?: string[];
  offset?: number;
  limit?: number;
};

const createGetUserActivityQuery = async (params: LaunchpadParams) => {
  const result = await getRaw(USER_ACTIVITY_URL, {
    params,
  });

  return result?.data;
};

const getActivityItemsFromPages = (pages: LaunchpadResponse[]) => {
  return pages?.flatMap((page) => page.results) ?? [];
};

export const useGetLaunchpad = (
  params: LaunchpadParams,
  options?: UseInfiniteQueryOptions<LaunchpadResponse>,
  {
    initialLimit = INITIAL_DISPLAY_MAX_COUNT,
    maxLimit = MAX_DISPLAY_COUNT,
  }: {
    initialLimit?: number;
    maxLimit?: number;
  } = {},
) => {
  const maxCount = maxLimit - initialLimit;
  const { data, error, fetchNextPage, hasNextPage, isFetchingNextPage, status } =
    useInfiniteQuery<LaunchpadResponse>({
      queryKey: [USER_ACTIVITY_URL, params],
      queryFn: ({ pageParam = 1 }) => {
        const isFirstPage = pageParam === 1;
        const requestParams = {
          ...params,
          offset: isFirstPage ? 0 : initialLimit,
          limit: isFirstPage ? initialLimit : maxCount,
        };

        return createGetUserActivityQuery(requestParams);
      },
      ...options,
      getNextPageParam: (lastPage, pages) => {
        const currentItems = getActivityItemsFromPages(pages);
        const current = currentItems.length ?? 0;
        const total = maxLimit > lastPage?.total ? lastPage?.total : maxLimit;
        const nextPageParam = pages.length + 1;

        return current >= total ? undefined : nextPageParam;
      },
    });

  const { pages = [] } = data ?? {};
  const activityItems = useMemo(() => getActivityItemsFromPages(pages), [pages]);
  const activityItemsCount = pages[0]?.total ?? 0;

  return {
    activityItems,
    activityItemsCount,
    hasError: !!error,
    isLoading: status === LOADING_STATUS,
    isLoadingMore: isFetchingNextPage,
    fetchNextPage,
    allItemsFetched: !hasNextPage,
  };
};
